import { useEffect, useState } from "react";
import { AdminLayout } from "@/components/Layouts";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { ThumbnailUploader } from "@/components/ThumbnailUploader";
import { CheckCircle2, AlertTriangle, Mail, RefreshCw, Send, RotateCw, X } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";

export default function AdminSettings() {
  const [form, setForm] = useState({
    platform_name: "",
    tagline: "",
    logo_url: "",
    brand_color: "#1ec7a0",
    contact_email: "",
    footer_disclaimer: "",
    video_token_ttl_mode: "auto",
  });
  const [emailStatus, setEmailStatus] = useState(null);
  const [emailLoading, setEmailLoading] = useState(false);
  const [testTo, setTestTo] = useState("");
  const [sending, setSending] = useState(false);
  const [lastResult, setLastResult] = useState(null); // { ok, message, resend_id, error }
  const [failedSetups, setFailedSetups] = useState([]);
  const [failedLoading, setFailedLoading] = useState(false);
  const [retryingId, setRetryingId] = useState(null);

  useEffect(() => {
    api.get("/settings").then((r) => setForm((f) => ({ ...f, ...r.data }))).catch(() => {});
    refreshEmailStatus();
    refreshFailedSetups();
  }, []);

  const refreshEmailStatus = async () => {
    setEmailLoading(true);
    try {
      const { data } = await api.get("/admin/email/status");
      setEmailStatus(data);
    } catch (e) {
      setEmailStatus({ configured: false, error: e?.response?.data?.detail || e.message });
    } finally {
      setEmailLoading(false);
    }
  };

  const refreshFailedSetups = async () => {
    setFailedLoading(true);
    try {
      const { data } = await api.get("/admin/email/failed-setups?since_days=14&limit=50");
      setFailedSetups(Array.isArray(data) ? data : []);
    } catch (e) {
      // Non-fatal: panel just stays empty
      setFailedSetups([]);
    } finally {
      setFailedLoading(false);
    }
  };

  const retryFailed = async (row) => {
    if (!row?.user_id) return;
    setRetryingId(row.user_id);
    try {
      const { data } = await api.post(`/admin/email/retry/${row.user_id}`);
      toast.success(`Setup link re-sent to ${data.email}.`, {
        description: data.resend_id ? `Resend ID: ${data.resend_id}` : undefined,
        duration: 6000,
      });
      // Auto-remove from local list (backend will hide it on next refresh too)
      setFailedSetups((rows) => rows.filter((r) => r.user_id !== row.user_id));
    } catch (e) {
      const detail = e?.response?.data?.detail;
      toast.error("Retry failed", {
        description: typeof detail === "string" ? detail : "See backend logs.",
        duration: 9000,
      });
    } finally {
      setRetryingId(null);
    }
  };

  const dismissFailed = async (row) => {
    if (!row?.attempt_id) return;
    try {
      await api.delete(`/admin/email/failed-setups/${row.attempt_id}`);
      setFailedSetups((rows) => rows.filter((r) => r.attempt_id !== row.attempt_id));
    } catch {
      toast.error("Failed to dismiss the entry.");
    }
  };

  const save = async () => {
    try {
      await api.patch("/admin/settings", form);
      toast.success("Settings saved");
    } catch { toast.error("Failed to save"); }
  };

  const sendTestEmail = async () => {
    const to = (testTo || "").trim();
    if (to && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(to)) {
      toast.error("Please enter a valid email address.");
      return;
    }
    setSending(true);
    setLastResult(null);
    try {
      const payload = to ? { to } : {};
      const { data } = await api.post("/admin/email/test", payload);
      setLastResult({ ok: true, message: data.message, resend_id: data.resend_id, to: data.to });
      toast.success(`Test email sent to ${data.to}`, {
        description: data.resend_id ? `Resend ID: ${data.resend_id}` : "Check the inbox.",
        duration: 7000,
      });
    } catch (e) {
      const detail = e?.response?.data?.detail;
      const msg = typeof detail === "string" && detail ? detail : "Failed to send test email.";
      setLastResult({ ok: false, error: msg });
      toast.error("Failed to send test email", { description: msg, duration: 9000 });
    } finally {
      setSending(false);
    }
  };

  return (
    <AdminLayout title="Settings">
      <div className="mb-5">
        <h1 className="text-3xl font-heading font-bold tracking-tight">Settings</h1>
        <p className="mt-1 text-muted-foreground">Platform settings for eLearningSaham.</p>
      </div>

      <div className="grid gap-5 max-w-2xl">
        <Card className="glass-card">
          <CardContent className="p-6 sm:p-8 space-y-5">
            <h2 className="text-lg font-heading font-semibold">Branding & Platform</h2>
            <div><Label>Platform Name</Label><Input value={form.platform_name} onChange={(e) => setForm({ ...form, platform_name: e.target.value })} data-testid="settings-platform-name" /></div>
            <div><Label>Tagline</Label><Input value={form.tagline} onChange={(e) => setForm({ ...form, tagline: e.target.value })} /></div>
            <ThumbnailUploader value={form.logo_url} onChange={(v) => setForm({ ...form, logo_url: v })} kind="logo" label="Logo" />
            <div className="grid grid-cols-2 gap-3">
              <div><Label>Brand Color</Label><Input type="color" value={form.brand_color} onChange={(e) => setForm({ ...form, brand_color: e.target.value })} className="h-10" /></div>
              <div><Label>Contact Email</Label><Input type="email" value={form.contact_email} onChange={(e) => setForm({ ...form, contact_email: e.target.value })} /></div>
            </div>
            <div><Label>Footer Disclaimer</Label><Textarea rows={3} value={form.footer_disclaimer} onChange={(e) => setForm({ ...form, footer_disclaimer: e.target.value })} /></div>
          </CardContent>
        </Card>

        {/* ========== Email Settings ========== */}
        <Card className="glass-card" data-testid="email-settings-card">
          <CardContent className="p-6 sm:p-8 space-y-5">
            <div className="flex items-start justify-between gap-2 flex-wrap">
              <div>
                <h2 className="text-lg font-heading font-semibold flex items-center gap-2">
                  <Mail className="h-5 w-5 text-primary" /> Email Settings
                </h2>
                <p className="mt-1 text-sm text-muted-foreground">
                  Configure Resend email delivery for password setup, password reset, and student notifications.
                </p>
              </div>
              <Button
                variant="ghost"
                size="sm"
                onClick={refreshEmailStatus}
                disabled={emailLoading}
                data-testid="email-refresh-status-btn"
              >
                <RefreshCw className={`h-3.5 w-3.5 ${emailLoading ? "animate-spin" : ""}`} /> Refresh
              </Button>
            </div>

            <div className="rounded-lg border border-border bg-muted/30 p-4 space-y-2 text-sm" data-testid="email-status-panel">
              {emailStatus === null ? (
                <p className="text-muted-foreground">Loading email service status...</p>
              ) : emailStatus.configured ? (
                <>
                  <div className="flex items-center gap-2 text-teal" data-testid="email-status-ok">
                    <CheckCircle2 className="h-4 w-4" />
                    <span className="font-medium">Email service is configured.</span>
                  </div>
                  <p className="text-xs text-muted-foreground"><strong>API Key:</strong> {emailStatus.api_key_masked}</p>
                  <p className="text-xs text-muted-foreground"><strong>Sender:</strong> {emailStatus.from_email}</p>
                  <p className="text-xs text-muted-foreground"><strong>Frontend URL:</strong> {emailStatus.frontend_url}</p>
                </>
              ) : (
                <div className="flex items-start gap-2 text-amber-400" data-testid="email-status-not-configured">
                  <AlertTriangle className="h-4 w-4 shrink-0 mt-0.5" />
                  <span>Email service is not configured. Please add <code className="font-mono">RESEND_API_KEY</code> to the backend environment.</span>
                </div>
              )}
            </div>

            <div className="space-y-3">
              <Label htmlFor="test-to">Send Test Email</Label>
              <div className="flex gap-2 flex-wrap">
                <Input
                  id="test-to"
                  type="email"
                  placeholder="leave blank to send to your own admin email"
                  value={testTo}
                  onChange={(e) => setTestTo(e.target.value)}
                  className="flex-1 min-w-[200px]"
                  data-testid="email-test-to-input"
                />
                <Button
                  onClick={sendTestEmail}
                  disabled={sending || !emailStatus?.configured}
                  data-testid="email-send-test-btn"
                >
                  <Send className="h-3.5 w-3.5" /> {sending ? "Sending..." : "Send Test Email"}
                </Button>
              </div>
              {lastResult && (
                <div
                  className={`rounded-lg border p-3 text-xs ${lastResult.ok
                    ? "border-teal/40 bg-teal/10 text-teal"
                    : "border-destructive/40 bg-destructive/10 text-destructive-foreground"}`}
                  data-testid="email-test-result"
                >
                  {lastResult.ok ? (
                    <>
                      <p className="font-semibold">Test email sent successfully.</p>
                      <p className="mt-1 opacity-80">Recipient: {lastResult.to}</p>
                      {lastResult.resend_id && <p className="opacity-80">Resend ID: <code className="font-mono">{lastResult.resend_id}</code></p>}
                    </>
                  ) : (
                    <>
                      <p className="font-semibold">Failed to send test email.</p>
                      <p className="mt-1 opacity-90">{lastResult.error}</p>
                      <p className="mt-2 opacity-70">
                        Common causes: sender domain not verified on Resend, invalid API key, free-tier daily quota exceeded, or recipient blocked.
                      </p>
                    </>
                  )}
                </div>
              )}
            </div>

            {/* ========== Recent Failed Sends ========== */}
            <div className="space-y-3 pt-4 border-t border-border" data-testid="failed-setups-section">
              <div className="flex items-center justify-between gap-2 flex-wrap">
                <div>
                  <h3 className="text-sm font-heading font-semibold flex items-center gap-2">
                    <AlertTriangle className="h-4 w-4 text-amber-400" /> Recent Failed Setup Sends
                    {failedSetups.length > 0 && (
                      <span className="ml-1 inline-flex items-center justify-center min-w-[20px] h-5 px-1.5 rounded-full bg-destructive text-destructive-foreground text-[10px] font-bold" data-testid="failed-setups-count">
                        {failedSetups.length}
                      </span>
                    )}
                  </h3>
                  <p className="mt-1 text-xs text-muted-foreground">
                    Students whose most recent setup-link send failed in the last 14 days.
                  </p>
                </div>
                <Button
                  variant="ghost"
                  size="sm"
                  onClick={refreshFailedSetups}
                  disabled={failedLoading}
                  data-testid="failed-setups-refresh-btn"
                >
                  <RefreshCw className={`h-3.5 w-3.5 ${failedLoading ? "animate-spin" : ""}`} /> Refresh
                </Button>
              </div>

              {failedSetups.length === 0 ? (
                <div className="rounded-lg border border-dashed border-border p-4 text-center text-xs text-muted-foreground" data-testid="failed-setups-empty">
                  {failedLoading ? "Loading…" : "No failed sends in the last 14 days."}
                </div>
              ) : (
                <div className="rounded-lg border border-border overflow-hidden" data-testid="failed-setups-list">
                  <ul className="divide-y divide-border">
                    {failedSetups.map((row) => (
                      <li key={row.attempt_id} className="p-3 sm:p-4 flex flex-col sm:flex-row sm:items-center gap-3 hover:bg-muted/30" data-testid={`failed-setup-row-${row.user_id}`}>
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">{row.name || "Unknown"}</p>
                          <p className="text-xs text-muted-foreground truncate">{row.email}</p>
                          {row.batch && <p className="text-[11px] text-muted-foreground/70">Batch: {row.batch}</p>}
                          <p className="mt-1 text-xs text-destructive-foreground/90 line-clamp-2" title={row.error || ""}>
                            <span className="font-semibold opacity-80">Error: </span>{row.error || "Unknown error"}
                          </p>
                          <p className="mt-1 text-[10px] uppercase tracking-wider text-muted-foreground/70">
                            {new Date(row.failed_at).toLocaleString()}
                          </p>
                        </div>
                        <div className="flex items-center gap-2 flex-shrink-0">
                          <Button
                            size="sm"
                            onClick={() => retryFailed(row)}
                            disabled={retryingId === row.user_id || row.access_status === "inactive" || row.access_status === "suspended"}
                            data-testid={`retry-failed-${row.user_id}`}
                          >
                            <RotateCw className={`h-3 w-3 ${retryingId === row.user_id ? "animate-spin" : ""}`} /> {retryingId === row.user_id ? "Retrying..." : "Retry"}
                          </Button>
                          <Button
                            size="icon"
                            variant="ghost"
                            onClick={() => dismissFailed(row)}
                            data-testid={`dismiss-failed-${row.attempt_id}`}
                            aria-label="Dismiss"
                            className="h-7 w-7"
                            title="Dismiss"
                          >
                            <X className="h-3.5 w-3.5" />
                          </Button>
                        </div>
                      </li>
                    ))}
                  </ul>
                </div>
              )}
            </div>
          </CardContent>
        </Card>

        <Card className="glass-card" data-testid="video-security-card">
          <CardContent className="p-6 sm:p-8 space-y-4">
            <div>
              <h2 className="text-lg font-heading font-semibold">Video Security</h2>
              <p className="mt-1 text-sm text-muted-foreground">
                Configure security tokens for class recording access.
              </p>
            </div>
            <div>
              <Label htmlFor="ttl-mode">Video security token validity</Label>
              <Select
                value={form.video_token_ttl_mode || "auto"}
                onValueChange={(v) => setForm({ ...form, video_token_ttl_mode: v })}
              >
                <SelectTrigger id="ttl-mode" className="mt-1.5" data-testid="ttl-mode-select">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10 minutes</SelectItem>
                  <SelectItem value="30">30 minutes</SelectItem>
                  <SelectItem value="60">60 minutes</SelectItem>
                  <SelectItem value="auto">Auto-refresh during playback (recommended)</SelectItem>
                </SelectContent>
              </Select>
              <p className="mt-2 text-xs text-muted-foreground leading-relaxed">
                Security tokens are used to protect video access. They <strong>do not limit
                viewing duration</strong>. Videos can be watched in full as long as the student's access is active.
                The system refreshes tokens automatically in the background without disrupting
                the video player.
              </p>
            </div>
          </CardContent>
        </Card>

        <div>
          <Button onClick={save} className="btn-glow" data-testid="save-settings-btn">Save Settings</Button>
        </div>
      </div>
    </AdminLayout>
  );
}
